/**
 * Shared formatting + badges for Meta performance metrics (#3824 Phase 7).
 *
 * Used by the campaign detail panel (Phase 5 card metrics) and the Meta dashboards. `ctr` / `frequency`
 * arrive as strings (backend Decimal serialises to a string), so they're coerced here.
 */

import type { MetaInsightsWindow, MetaPerformanceStatus } from '@/lib/types';

const NO_VALUE = '—';

/** A cents amount in the supplied currency symbol; `—` when null. */
export function formatCents(cents: number | null | undefined, symbol: string): string {
  if (cents == null) return NO_VALUE;
  return `${symbol}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** A pacing percentage to one decimal (e.g. "62.5%"); `—` when null. */
export function formatPacing(pct: number | null | undefined): string {
  if (pct == null) return NO_VALUE;
  return `${pct.toFixed(1)}%`;
}

/** CTR as a percentage to two decimals (e.g. "1.20%"); `—` when missing. */
export function formatCtr(ctr?: string | null): string {
  if (ctr == null) return NO_VALUE;
  const value = Number(ctr);
  return Number.isFinite(value) ? `${value.toFixed(2)}%` : NO_VALUE;
}

/** Frequency to two decimals; `—` when missing. */
export function formatFrequency(frequency?: string | null): string {
  if (frequency == null) return NO_VALUE;
  const value = Number(frequency);
  return Number.isFinite(value) ? value.toFixed(2) : NO_VALUE;
}

/** Short date from a `YYYY-MM-DD` string, parsed as local to avoid a UTC day-shift. */
function formatShortDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Human label for the reporting window, e.g. "Last 7 days: May 1 to May 7". */
export function formatWindowLabel(window: MetaInsightsWindow | null | undefined): string | null {
  if (!window) return null;
  const [sy, sm, sd] = window.date_start.split('-').map(Number);
  const [ey, em, ed] = window.date_stop.split('-').map(Number);
  const days = Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86_400_000) + 1;
  return `Last ${days} days: ${formatShortDate(window.date_start)} to ${formatShortDate(window.date_stop)}`;
}

const PERFORMANCE_BADGE: Record<MetaPerformanceStatus, { label: string; className: string }> = {
  WINNER: { label: 'Winner', className: 'text-[#047857] bg-[#D1FAE5] border-[#6EE7B7]' },
  LOSER: { label: 'Loser', className: 'text-[#B91C1C] bg-[#FEE2E2] border-[#FCA5A5]' },
  ON_THE_FENCE: { label: 'On the Fence', className: 'text-[#A16207] bg-[#FEF3C7] border-[#FDE68A]' },
};

/** Pill for an ad's detector verdict; renders nothing when there's no current verdict. */
export function MetaPerformanceBadge({ status }: { status?: MetaPerformanceStatus | null }) {
  if (!status) return null;
  const { label, className } = PERFORMANCE_BADGE[status];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${className}`}>
      {label}
    </span>
  );
}

/** SCREAMING_SNAKE Meta enum → human Title Case, e.g. OFFSITE_CONVERSIONS → "Offsite Conversions". */
export function humanizeMetaEnum(value: string | null | undefined): string {
  // "Not set", never an em-dash: dashes are banned in UI copy (frontend/CLAUDE.md, #6179).
  if (!value) return 'Not set';
  return value
    .replace(/_/g, ' ')
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}
